// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import ProgressBar from './ProgressBar';
import './SystemStatus.css';
import * as ColorUtils from '../utils/ui/ColorUtils';
import ShipVisualization from './ShipVisualization';
import { useShipHealth } from '../contexts/ShipHealthContext';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import { useCredits } from '../contexts/CreditsContext';
import { useUpgrades } from '../contexts/UpgradesContext';
import { useFuel } from '../contexts/FuelContext';
import GameConfig from '../config/GameConfig';
import { getDifficultyMultipliers } from '../utils/DifficultyUtils';

const SystemStatus = ({ power, currentAlert, onClose, onOpenAlertHistory, isOpen = true }) => {
  const { canAfford, removeCredits } = useCredits();
  const { systems: shipSystems, getOverallHealth, repairSystem, repairAll } = useShipHealth();
  const { getPurchasedUpgrades } = useUpgrades();
  const { getTankUpgrades } = useFuel();
  
  const [systems, setSystems] = useState([]);
  const [shipRotation, setShipRotation] = useState(0);
  const [sparks, setSparks] = useState([]);
  const [repairCost, setRepairCost] = useState(0);
  const [overallHealth, setOverallHealth] = useState(100);
  const [highlightedSystem, setHighlightedSystem] = useState(null);
  const [showRepairConfirm, setShowRepairConfirm] = useState(false);
  const [repairTarget, setRepairTarget] = useState(null);
  const [installedUpgrades, setInstalledUpgrades] = useState([]);

  // Calculate repair cost for a system
  const calculateSystemRepairCost = (system) => {
    const damage = system.maxHealth - system.health;
    return Math.ceil(damage * GameConfig.credits.repairCostPerHP * getDifficultyMultipliers().repairCostMultiplier);
  };

  // Calculate total repair cost
  const calculateTotalRepairCost = (systemsList) => {
    const totalDamage = systemsList.reduce((sum, sys) => {
      return sum + (sys.maxHealth - sys.health);
    }, 0);
    return Math.ceil(totalDamage * GameConfig.credits.repairCostPerHP * getDifficultyMultipliers().repairCostMultiplier);
  };

  // Load ship systems and subscribe to changes
  useEffect(() => {
    const updateSystems = () => {
      setSystems(shipSystems.map((sys, index) => {
        const criticalThreshold = GameConfig.shipHealth.criticalThresholds[sys.id.toLowerCase()] || 30;
        return {
          ...sys,
          value: sys.health || 0,
          status: sys.health <= 0 ? 'DESTROYED' : 
                  sys.health <= criticalThreshold ? 'CRITICAL' :
                  sys.health < sys.maxHealth ? 'DAMAGED' : 'OPTIMAL',
          damaged: sys.health < sys.maxHealth,
          critical: sys.health <= criticalThreshold,
          // Positions optimized for rotated ship (90deg) with scaleY(2.2)
          x: [50, 50, 20, 50, 50, 80, 50][index] || 50,  
          y: [50, 20, 70, 80, 30, 70, 40][index] || 50
        };
      }));
      
      // Calculate repair cost
      setRepairCost(calculateTotalRepairCost(shipSystems));
      
      // Update overall health
      setOverallHealth(getOverallHealth());
      
      // Create sparks for damaged systems
      shipSystems.forEach((sys, index) => {
        const criticalThreshold = GameConfig.shipHealth.criticalThresholds[sys.id.toLowerCase()] || 30;
        const isCritical = sys.health <= criticalThreshold;
        if (isCritical && Math.random() < GameConfig.ui.sparkGenerationChance) {
          const sparkId = `${Date.now()}-${Math.random()}`;
          const positions = [[50, 50], [30, 50], [70, 50], [50, 30], [50, 70], [35, 35], [65, 65]];
          setSparks(prev => [...prev, { 
            id: sparkId, 
            x: positions[index][0], 
            y: positions[index][1] 
          }]);
          setTimeout(() => {
            setSparks(prev => prev.filter(s => s.id !== sparkId));
          }, 1000);
        }
      });
    };
    
    updateSystems();
    const interval = setInterval(updateSystems, 1000);
    
    // Load installed upgrades
    const purchasedIds = getPurchasedUpgrades();
    const purchased = [];
    
    // Add ship upgrades from UpgradesContext
    Object.entries(GameConfig.upgrades).forEach(([category, upgrades]) => {
      if (Array.isArray(upgrades)) {
        upgrades.forEach(upgrade => {
          if (purchasedIds.includes(upgrade.id)) {
            purchased.push({
              ...upgrade,
              category: category.charAt(0).toUpperCase() + category.slice(1)
            });
          }
        });
      }
    });
    
    // Add tank upgrades from FuelContext
    const tankUpgradeCount = getTankUpgrades();
    if (tankUpgradeCount > 0) {
      for (let i = 0; i < tankUpgradeCount; i++) {
        purchased.push({
          id: `tank_upgrade_${i + 1}`,
          name: `Fuel Tank Expansion ${i + 1}`,
          icon: '⛽',
          category: 'Fuel',
          tier: i + 1,
          description: '+25% fuel capacity'
        });
      }
    }
    
    setInstalledUpgrades(purchased);
    
    return () => {
      clearInterval(interval);
    };
  }, [shipSystems, getOverallHealth, getPurchasedUpgrades, getTankUpgrades]);

  // Ship rotation animation - rock back and forth
  useEffect(() => {
    let angle = 0;
    let direction = 1;
    const rotationInterval = setInterval(() => {
      angle += direction * 0.5;
      if (angle >= 15) direction = -1; // Rock to +15 degrees
      if (angle <= -15) direction = 1;  // Rock to -15 degrees
      setShipRotation(angle);
    }, 30);
    return () => clearInterval(rotationInterval);
  }, []);

  // Use ColorUtils for consistent color coding
  const getStatusColor = ColorUtils.getSystemStatusColor;
  // eslint-disable-next-line no-unused-vars
  const getValueColor = ColorUtils.getResourceColor;

  const handleClose = () => {
    // Dispatch event to notify other components
    EventBus.emit(EVENT_TYPES.SYSTEM_STATUS_CLOSED);
    onClose();
  };

  const handleRepairAll = () => {
    const cost = calculateTotalRepairCost(shipSystems);
    
    if (!canAfford(cost)) {
      setRepairTarget({ 
        type: 'all', 
        name: 'ALL SYSTEMS', 
        cost, 
        error: 'INSUFFICIENT CREDITS' 
      });
      setShowRepairConfirm(true);
      return;
    }
    
    if (cost === 0) {
      setRepairTarget({ 
        type: 'all', 
        name: 'ALL SYSTEMS', 
        cost: 0, 
        error: 'ALL SYSTEMS OPTIMAL' 
      });
      setShowRepairConfirm(true);
      return;
    }
    
    setRepairTarget({ 
      type: 'all', 
      name: 'ALL SYSTEMS', 
      cost 
    });
    setShowRepairConfirm(true);
  };

  const handleRepairSystem = (systemId) => {
    const system = systems.find(s => s.id === systemId);
    if (!system) return;
    
    const cost = calculateSystemRepairCost(system);
    const damage = system.maxHealth - system.health;
    
    if (damage === 0) {
      setRepairTarget({ 
        type: 'single', 
        systemId, 
        name: system.name, 
        cost: 0, 
        error: 'ALREADY AT FULL HEALTH' 
      });
      setShowRepairConfirm(true);
      return;
    }
    
    if (!canAfford(cost)) {
      setRepairTarget({ 
        type: 'single', 
        systemId, 
        name: system.name, 
        cost, 
        error: 'INSUFFICIENT CREDITS' 
      });
      setShowRepairConfirm(true);
      return;
    }
    
    setRepairTarget({ 
      type: 'single', 
      systemId, 
      name: system.name, 
      cost 
    });
    setShowRepairConfirm(true);
  };

  const confirmRepair = () => {
    if (!repairTarget || repairTarget.error) {
      setShowRepairConfirm(false);
      setRepairTarget(null);
      return;
    }

    // Deduct credits
    removeCredits(repairTarget.cost, repairTarget.type === 'all' ? 'repair all systems' : `repair ${repairTarget.name}`);

    // Perform repair
    if (repairTarget.type === 'all') {
      repairAll();
    } else {
      repairSystem(repairTarget.systemId);
    }
    
    setShowRepairConfirm(false);
    setRepairTarget(null);
  };

  const cancelRepair = () => {
    setShowRepairConfirm(false);
    setRepairTarget(null);
  };

  const handleSystemClick = (systemId) => {
    setHighlightedSystem(systemId);
    
    // Scroll to the system card
    setTimeout(() => {
      const element = document.getElementById(`system-card-${systemId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
    
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedSystem(null);
    }, 3000);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="◊ SHIP SYSTEMS STATUS ◊"
        size="large"
        className="system-status-modal"
      >
        <div className="system-status-content">
          {/* Overall Health */}
          <div className="overall-health-section">
            <div className="overall-health-label">OVERALL SHIP HEALTH</div>
            <ProgressBar 
              value={overallHealth}
              max={100}
              color="auto"
              showLabel={true}
              animated={false}
              size="large"
            />
          </div>

          {/* Ship Visualization */}
          <div className="ship-visualization-container">
            <ShipVisualization 
              systems={systems}
              rotation={shipRotation}
              sparks={sparks}
              onSystemClick={handleSystemClick}
            />
          </div>

          {/* Systems List */}
          <div className="systems-list">
            <div className="systems-list-header">SYSTEM DIAGNOSTICS</div>
            <div className="systems-grid">
              {systems.map(system => {
                const health = system.health || 0;
                const maxHealth = system.maxHealth || 100;
                const damage = maxHealth - health;
                const systemRepairCost = Math.ceil(damage * GameConfig.credits.repairCostPerHP);
                const displayValue = system.value || 0;
                
                return (
                  <div 
                    key={system.id}
                    id={`system-card-${system.id}`}
                    className={`system-item ${system.damaged ? 'damaged' : ''} ${system.critical ? 'critical' : ''} ${highlightedSystem === system.id ? 'highlighted' : ''} ${damage > 0 ? 'repairable' : ''}`}
                    onClick={() => damage > 0 && handleRepairSystem(system.id)}
                    style={{ cursor: damage > 0 ? 'pointer' : 'default' }}
                  >
                    <div className="system-item-header">
                      <div className="system-item-name">{system.name}</div>
                      <div 
                        className="system-item-status"
                        style={{ color: getStatusColor(system.status) }}
                      >
                        {system.status}
                      </div>
                    </div>
                    <ProgressBar 
                      value={displayValue}
                      max={100}
                      color="auto"
                      showLabel={true}
                      animated={false}
                      size="small"
                    />
                    <div className="system-item-footer">
                      {damage > 0 && (
                        <div className="system-repair-cost">
                          🔧 {systemRepairCost} credits
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Repair Section */}
          <div className="repair-section">
            <div className="repair-info">
              <div className="repair-label">REPAIR ALL SYSTEMS</div>
              <div className="repair-cost">
                Cost: <span style={{ color: '#ffaa00' }}>{repairCost} CREDITS</span>
              </div>
            </div>
            <Button 
              variant={repairCost === 0 ? "success" : "warning"}
              size="large"
              onClick={handleRepairAll}
              disabled={repairCost === 0}
            >
              {repairCost === 0 ? 'ALL SYSTEMS OPTIMAL' : 'INITIATE REPAIRS'}
            </Button>
          </div>

          {/* Installed Upgrades Section */}
          {installedUpgrades.length > 0 && (
            <div className="upgrades-section">
              <div className="upgrades-header">🔧 INSTALLED UPGRADES</div>
              <div className="upgrades-list">
                {installedUpgrades.map(upgrade => (
                  <div key={upgrade.id} className="upgrade-item">
                    <div className="upgrade-icon">{upgrade.icon}</div>
                    <div className="upgrade-info">
                      <div className="upgrade-name">{upgrade.name}</div>
                      <div className="upgrade-category">{upgrade.category}</div>
                    </div>
                    <div className="upgrade-status">✓ ACTIVE</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Penalties Warning */}
          {overallHealth < 80 && (
            <div className="penalties-warning">
              <div className="penalties-title">⚠ PERFORMANCE DEGRADATION</div>
              <div className="penalties-list">
                {systems.find(s => s.id === 'ENGINES')?.health < 50 && (
                  <div className="penalty-item">• Warp speed reduced by 50%</div>
                )}
                {systems.find(s => s.id === 'WEAPONS')?.health < 50 && (
                  <div className="penalty-item">• Weapon damage reduced by 30%</div>
                )}
                {systems.find(s => s.id === 'SENSORS')?.health < 50 && (
                  <div className="penalty-item">• Scan rewards reduced by 50%</div>
                )}
                {systems.find(s => s.id === 'POWER')?.health < 50 && (
                  <div className="penalty-item">• Fuel consumption increased by 50%</div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Repair Confirmation Modal */}
      {showRepairConfirm && repairTarget && (
        <Modal
          isOpen={showRepairConfirm}
          onClose={cancelRepair}
          title={repairTarget.error ? '⚠ REPAIR UNAVAILABLE' : '🔧 CONFIRM REPAIR'}
          size="small"
          className="repair-confirm-modal"
        >
          <div className="repair-confirm-content">
            <div className="repair-confirm-system">{repairTarget.name}</div>
            
            {repairTarget.error ? (
              <div className="repair-confirm-error">{repairTarget.error}</div>
            ) : (
              <>
                <div className="repair-confirm-cost">
                  <span className="repair-cost-label">CREDITS COST:</span>
                  <span className="repair-cost-value">{repairTarget.cost}</span>
                </div>
                <div className="repair-confirm-message">
                  Repair {repairTarget.type === 'all' ? 'all damaged systems' : 'this system'} to 100% health?
                </div>
              </>
            )}
          </div>
          
          <div className="repair-confirm-actions">
            {!repairTarget.error && (
              <Button 
                variant="success"
                size="large"
                onClick={confirmRepair}
              >
                CONFIRM REPAIR
              </Button>
            )}
            <Button 
              variant="secondary"
              size="large"
              onClick={cancelRepair}
            >
              {repairTarget.error ? 'CLOSE' : 'CANCEL'}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SystemStatus;
